import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Konverter Ukuran Memasak - Konversi Cangkir, Sendok, Gram, dan Ons";
const DESCRIPTION =
  "Konverter ukuran memasak gratis - konversi antara cangkir, sendok makan, sendok teh, gram, ons, dan mililiter dengan akurat. Ideal untuk memasak, memanggang, dan menyiapkan resep. Dapatkan hasilnya seketika.";
const KEYWORDS =
  "konverter ukuran memasak, konversi satuan memasak, berapa sendok dalam satu cangkir, konversi gram ke cangkir, konversi ons ke mililiter, satuan ukuran dapur, cooking measurement converter, konversi resep masak";
const OG_URL = "https://do-calculate.com/calculator/id/cooking-measurement/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Konverter Ukuran Memasak",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Konverter Ukuran Memasak",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa sendok makan dalam satu cangkir?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Satu cangkir mengandung 16 sendok makan. Artinya seperempat cangkir sama dengan 4 sendok makan, dan setengah cangkir sama dengan 8 sendok makan.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara mengonversi cangkir ke mililiter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Satu cangkir AS standar sama dengan sekitar 236,588 ml. Untuk mengonversi cangkir ke mililiter, kalikan jumlah cangkir dengan 236,588. Contoh: 2 cangkir = 473,176 ml.",
        },
      },
      {
        "@type": "Question",
        name: "Apa perbedaan antara ons cair dan ons berat?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ons cair (fl oz) adalah satuan volume yang sama dengan 29,5735 ml, sedangkan ons berat (oz) adalah satuan massa yang sama dengan 28,3495 gram. Yang pertama digunakan untuk cairan dan yang kedua untuk bahan kering.",
        },
      },
    ],
  },
];

// Conversion factors to milliliters (ml) for volume units
const VOLUME_UNITS = {
  ml: { factor: 1, name: "Mililiter (ml)" },
  tsp: { factor: 4.92892, name: "Sendok teh (tsp)" },
  tbsp: { factor: 14.7868, name: "Sendok makan (tbsp)" },
  fl_oz: { factor: 29.5735, name: "Ons cair (fl oz)" },
  cup: { factor: 236.588, name: "Cangkir AS (cup)" },
  pint: { factor: 473.176, name: "Pint (pint)" },
  quart: { factor: 946.353, name: "Quart (quart)" },
  gallon: { factor: 3785.41, name: "Galon (gallon)" },
  liter: { factor: 1000, name: "Liter (L)" },
};

// Conversion factors to grams for weight units
const WEIGHT_UNITS = {
  g: { factor: 1, name: "Gram (g)" },
  kg: { factor: 1000, name: "Kilogram (kg)" },
  oz: { factor: 28.3495, name: "Ons (oz)" },
  lb: { factor: 453.592, name: "Pon (lb)" },
};

// Common ingredients with density (g/ml) for volume ↔ weight conversion
const INGREDIENTS = {
  water: { name: "Air", density: 1.0 },
  milk: { name: "Susu", density: 1.03 },
  flour_ap: { name: "Tepung terigu serbaguna", density: 0.529 },
  flour_bread: { name: "Tepung roti", density: 0.529 },
  flour_cake: { name: "Tepung kue", density: 0.5 },
  sugar_white: { name: "Gula putih (butiran)", density: 0.845 },
  sugar_brown: { name: "Gula merah (padat)", density: 0.882 },
  sugar_powder: { name: "Gula halus (bubuk)", density: 0.56 },
  butter: { name: "Mentega", density: 0.911 },
  oil_veg: { name: "Minyak sayur", density: 0.917 },
  oil_olive: { name: "Minyak zaitun", density: 0.914 },
  honey: { name: "Madu", density: 1.42 },
  salt: { name: "Garam", density: 1.217 },
  baking_soda: { name: "Baking soda", density: 1.0 },
  baking_powder: { name: "Baking powder", density: 0.9 },
  cocoa: { name: "Kakao", density: 0.529 },
  rice: { name: "Beras mentah", density: 0.751 },
  oats: { name: "Gandum (oat)", density: 0.4 },
  cornstarch: { name: "Tepung maizena", density: 0.608 },
  cream: { name: "Krim masak", density: 0.994 },
  yogurt: { name: "Yogurt", density: 1.03 },
  syrup: { name: "Sirup / molase", density: 1.33 },
  vinegar: { name: "Cuka", density: 1.006 },
};

const CONVERSION_TYPES = {
  volume_to_volume: "Konversi Volume ke Volume",
  weight_to_weight: "Konversi Berat ke Berat",
  volume_to_weight: "Konversi Volume ke Berat",
  weight_to_volume: "Konversi Berat ke Volume",
};

function CookingMeasurementCalculator() {
  const [conversionType, setConversionType] = useState("volume_to_volume");
  const [fromUnit, setFromUnit] = useState("cup");
  const [toUnit, setToUnit] = useState("ml");
  const [ingredient, setIngredient] = useState("flour_ap");
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateResult = () => {
    setError("");
    setResult(null);

    const val = parseFloat(inputValue);
    if (!inputValue || isNaN(val) || val <= 0) {
      if (inputValue) {
        setError("Masukkan nilai yang valid dan positif");
      }
      return;
    }

    try {
      let convertedValue = 0;
      let formulaText = "";
      let fromUnitName = "";
      let toUnitName = "";
      let additionalInfo = [];

      if (conversionType === "volume_to_volume") {
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} ml/satuan ÷ ${toFactor} ml/satuan = ${convertedValue.toFixed(4)} ${toUnitName}`;

        const inMlFinal = val * fromFactor;
        additionalInfo.push({ label: "Dalam mililiter", value: `${inMlFinal.toFixed(2)} ml` });
        if (fromUnit !== "tsp" && toUnit !== "tsp") additionalInfo.push({ label: "Dalam sendok teh", value: `${(inMlFinal / VOLUME_UNITS.tsp.factor).toFixed(2)} tsp` });
        if (fromUnit !== "tbsp" && toUnit !== "tbsp") additionalInfo.push({ label: "Dalam sendok makan", value: `${(inMlFinal / VOLUME_UNITS.tbsp.factor).toFixed(2)} tbsp` });
        if (fromUnit !== "cup" && toUnit !== "cup") additionalInfo.push({ label: "Dalam cangkir AS", value: `${(inMlFinal / VOLUME_UNITS.cup.factor).toFixed(4)} cup` });
        if (fromUnit !== "fl_oz" && toUnit !== "fl_oz") additionalInfo.push({ label: "Dalam ons cair", value: `${(inMlFinal / VOLUME_UNITS.fl_oz.factor).toFixed(2)} fl oz` });

      } else if (conversionType === "weight_to_weight") {
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} × ${fromFactor} g/satuan ÷ ${toFactor} g/satuan = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "Dalam gram", value: `${inGrams.toFixed(2)} g` });
        if (fromUnit !== "kg" && toUnit !== "kg") additionalInfo.push({ label: "Dalam kilogram", value: `${(inGrams / 1000).toFixed(4)} kg` });
        if (fromUnit !== "oz" && toUnit !== "oz") additionalInfo.push({ label: "Dalam ons", value: `${(inGrams / WEIGHT_UNITS.oz.factor).toFixed(2)} oz` });
        if (fromUnit !== "lb" && toUnit !== "lb") additionalInfo.push({ label: "Dalam pon", value: `${(inGrams / WEIGHT_UNITS.lb.factor).toFixed(4)} lb` });

      } else if (conversionType === "volume_to_weight") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = VOLUME_UNITS[fromUnit].factor;
        const toFactor = WEIGHT_UNITS[toUnit].factor;
        fromUnitName = VOLUME_UNITS[fromUnit].name;
        toUnitName = WEIGHT_UNITS[toUnit].name;
        const inMl = val * fromFactor;
        const inGrams = inMl * density;
        convertedValue = inGrams / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inMl.toFixed(2)} ml × ${density} g/ml (kepadatan ${ingName}) = ${inGrams.toFixed(2)} g ÷ ${toFactor} = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "Bahan", value: ingName });
        additionalInfo.push({ label: "Kepadatan", value: `${density} g/ml` });
        additionalInfo.push({ label: "Volume dalam ml", value: `${inMl.toFixed(2)} ml` });
        additionalInfo.push({ label: "Dalam gram", value: `${inGrams.toFixed(2)} g` });
        additionalInfo.push({ label: "Dalam kilogram", value: `${(inGrams / 1000).toFixed(4)} kg` });

      } else if (conversionType === "weight_to_volume") {
        const density = INGREDIENTS[ingredient].density;
        const ingName = INGREDIENTS[ingredient].name;
        const fromFactor = WEIGHT_UNITS[fromUnit].factor;
        const toFactor = VOLUME_UNITS[toUnit].factor;
        fromUnitName = WEIGHT_UNITS[fromUnit].name;
        toUnitName = VOLUME_UNITS[toUnit].name;
        const inGrams = val * fromFactor;
        const inMl = inGrams / density;
        convertedValue = inMl / toFactor;
        formulaText = `${val} ${fromUnitName} = ${inGrams.toFixed(2)} g ÷ ${density} g/ml (kepadatan ${ingName}) = ${inMl.toFixed(2)} ml ÷ ${toFactor} = ${convertedValue.toFixed(4)} ${toUnitName}`;

        additionalInfo.push({ label: "Bahan", value: ingName });
        additionalInfo.push({ label: "Kepadatan", value: `${density} g/ml` });
        additionalInfo.push({ label: "Dalam mililiter", value: `${inMl.toFixed(2)} ml` });
        additionalInfo.push({ label: "Dalam sendok makan", value: `${(inMl / VOLUME_UNITS.tbsp.factor).toFixed(2)} tbsp` });
        additionalInfo.push({ label: "Dalam cangkir", value: `${(inMl / VOLUME_UNITS.cup.factor).toFixed(4)} cup` });
      }

      setResult({
        convertedValue,
        formulaText,
        fromUnitName: fromUnitName || fromUnit,
        toUnitName: toUnitName || toUnit,
        additionalInfo,
        inputValue: val,
      });
    } catch (err) {
      setError("Kesalahan perhitungan: " + err.message);
    }
  };

  useEffect(() => {
    if (inputValue) {
      calculateResult();
    } else {
      setResult(null);
      setError("");
    }
  }, [conversionType, fromUnit, toUnit, ingredient, inputValue]);

  useEffect(() => {
    if (conversionType === "volume_to_volume") {
      setFromUnit("cup");
      setToUnit("ml");
    } else if (conversionType === "weight_to_weight") {
      setFromUnit("g");
      setToUnit("oz");
    } else if (conversionType === "volume_to_weight") {
      setFromUnit("cup");
      setToUnit("g");
    } else if (conversionType === "weight_to_volume") {
      setFromUnit("g");
      setToUnit("ml");
    }
    setResult(null);
    setInputValue("");
    setError("");
  }, [conversionType]);

  const handleClear = () => {
    setInputValue("");
    setResult(null);
    setError("");
  };

  const isVolumeFrom =
    conversionType === "volume_to_volume" ||
    conversionType === "volume_to_weight";
  const isVolumeTo =
    conversionType === "volume_to_volume" ||
    conversionType === "weight_to_volume";
  const needsIngredient =
    conversionType === "volume_to_weight" ||
    conversionType === "weight_to_volume";

  const fromUnitsMap = isVolumeFrom ? VOLUME_UNITS : WEIGHT_UNITS;
  const toUnitsMap = isVolumeTo ? VOLUME_UNITS : WEIGHT_UNITS;

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Konverter Ukuran Memasak</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Konverter Ukuran Memasak</strong> adalah alat gratis dan akurat yang membantu Anda
                <strong> mengonversi satuan pengukuran</strong> antara cangkir, sendok
                makan, sendok teh, mililiter, gram, ons, dan pon dengan mudah. Baik Anda mengikuti resep Amerika maupun Eropa, dapatkan konversi yang tepat seketika. Sempurna untuk memasak, memanggang, dan menyiapkan kue dan minuman.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Konversi Ukuran Memasak dengan Mudah
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="conversionType">Jenis Konversi</label>
                  <select
                    id="conversionType"
                    className="form-select"
                    value={conversionType}
                    onChange={(e) => setConversionType(e.target.value)}
                  >
                    {Object.entries(CONVERSION_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {needsIngredient && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="ingredient">Bahan</label>
                    <select
                      id="ingredient"
                      className="form-select"
                      value={ingredient}
                      onChange={(e) => setIngredient(e.target.value)}
                    >
                      {Object.entries(INGREDIENTS).map(([key, { name }]) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="fromUnit">Dari Satuan</label>
                  <select
                    id="fromUnit"
                    className="form-select"
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                  >
                    {Object.entries(fromUnitsMap).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="toUnit">Ke Satuan</label>
                  <select
                    id="toUnit"
                    className="form-select"
                    value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}
                  >
                    {Object.entries(toUnitsMap).map(([key, { name }]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="inputValue">Jumlah</label>
                  <input
                    id="inputValue"
                    type="number"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    min="0"
                    step="0.001"
                    placeholder="Masukkan jumlah"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Hapus
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Keterangan</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah yang Dimasukkan</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {result.inputValue} {result.fromUnitName}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Nilai Terkonversi</td>
                          <td style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.convertedValue % 1 === 0
                              ? result.convertedValue.toFixed(0)
                              : result.convertedValue < 0.01
                              ? result.convertedValue.toFixed(6)
                              : result.convertedValue.toFixed(4)}{" "}
                            {result.toUnitName}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Konversi Cepat Tambahan</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          {result.additionalInfo.map((item, index) => (
                            <li key={index}>
                              🔄 <strong>{item.label}:</strong>{" "}
                              {item.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Perhitungan</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p><strong>Jenis Konversi:</strong>{" "}{CONVERSION_TYPES[conversionType]}</p>
                          <p><strong>Dari:</strong> {result.fromUnitName}</p>
                          <p><strong>Ke:</strong> {result.toUnitName}</p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}><strong>Metode Perhitungan:</strong></p>
                          <p style={{ fontSize: "13px", color: "#555", wordBreak: "break-word" }}>
                            {result.formulaText}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Konverter Ukuran Memasak?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Konverter Ukuran Memasak</strong> adalah alat elektronik gratis yang membebaskan Anda dari kerumitan konversi manual antara satuan pengukuran berbeda di dapur. Begitu Anda membuka resep asing, Anda langsung menghadapi cangkir, ons, dan pint — konverter ini menyelesaikan masalah tersebut dalam hitungan detik.
                </p>
                <p style={{ lineHeight: "1.8" }}>Alat ini mendukung empat jenis konversi:</p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Volume ke Volume:</strong> antara cangkir, sendok, mililiter, ons cair, pint, quart, galon, dan liter</li>
                  <li><strong>Berat ke Berat:</strong> antara gram, kilogram, ons, dan pon</li>
                  <li><strong>Volume ke Berat:</strong> konversi cangkir atau sendok ke gram atau pon berdasarkan bahan</li>
                  <li><strong>Berat ke Volume:</strong> konversi gram ke sendok atau cangkir berdasarkan kepadatan bahan</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Cara Menggunakan Konverter:</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih jenis konversi yang diinginkan</li>
                  <li>Jika Anda mengonversi antara volume dan berat, pilih bahan dari daftar</li>
                  <li>Pilih satuan asal dan satuan tujuan</li>
                  <li>Masukkan jumlah dan hasilnya akan muncul seketika</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 Catatan:</strong> Konversi volume ke berat bergantung pada kepadatan bahan, jadi pilih bahan yang benar untuk hasil yang akurat. Satu cangkir tepung tidak sama beratnya dengan satu cangkir gula!
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Kegunaan Konverter Ukuran Memasak</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Untuk Memasak Sehari-hari:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Mengikuti resep asing:</strong> Konversi cangkir Amerika ke satuan yang familiar secara lokal</li>
                  <li><strong>Menyesuaikan jumlah resep:</strong> Perbesar atau perkecil resep sambil menjaga proporsinya</li>
                  <li><strong>Mengganti alat ukur:</strong> Tidak ada timbangan? Konversi gram ke sendok dan cangkir</li>
                  <li><strong>Memasak sehat:</strong> Ukur jumlah dengan tepat untuk menghitung kalori</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Memanggang dan Membuat Kue:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Tepung dan gula:</strong> Resep memerlukan presisi tinggi dan konversi memastikan proporsi yang benar</li>
                  <li><strong>Bahan cair:</strong> Konversi mililiter susu, minyak, dan krim antar satuan</li>
                  <li><strong>Baking powder dan soda:</strong> Jumlah kecil yang membutuhkan konversi yang tepat</li>
                  <li><strong>Cokelat dan kakao:</strong> Konversi berat ke volume berdasarkan kepadatan</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Minuman dan Jus:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Membuat jus:</strong> Konversi cangkir ke mililiter atau liter dengan tepat</li>
                  <li><strong>Kopi dan teh:</strong> Mendapatkan rasio sempurna antara air dan bahan</li>
                  <li><strong>Koktail:</strong> Konversi ons cair ke mililiter untuk persiapan yang tepat</li>
                  <li><strong>Minuman olahraga:</strong> Ukur bubuk protein dan konversi antar satuan</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Profesional Kuliner:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Restoran dan kafe:</strong> Konversi resep dalam jumlah besar dengan cepat</li>
                  <li><strong>Pelatihan profesional:</strong> Pelajari konversi antara sistem pengukuran yang berbeda</li>
                  <li><strong>Standarisasi resep:</strong> Konversi satuan resep ke sistem pengukuran yang seragam</li>
                  <li><strong>Manajemen inventaris:</strong> Hitung jumlah bahan baku yang diperlukan</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tabel Satuan Ukuran Memasak Umum</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Berikut tabel referensi cepat untuk konversi terpenting antara satuan volume di dapur. Simpan untuk referensi jika dibutuhkan:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Satuan</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Dalam ml</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Setara dengan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Sendok teh (tsp)</td><td style={{ textAlign: "center" }}>4.93 ml</td><td style={{ textAlign: "center" }}>⅓ sendok makan</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Sendok makan (tbsp)</td><td style={{ textAlign: "center" }}>14.79 ml</td><td style={{ textAlign: "center" }}>3 sendok teh</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Ons cair (fl oz)</td><td style={{ textAlign: "center" }}>29.57 ml</td><td style={{ textAlign: "center" }}>2 sendok makan</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Cangkir AS (cup)</td><td style={{ textAlign: "center" }}>236.59 ml</td><td style={{ textAlign: "center" }}>16 sendok makan</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Pint (pint)</td><td style={{ textAlign: "center" }}>473.18 ml</td><td style={{ textAlign: "center" }}>2 cangkir</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Quart (quart)</td><td style={{ textAlign: "center" }}>946.35 ml</td><td style={{ textAlign: "center" }}>4 cangkir</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Galon (gallon)</td><td style={{ textAlign: "center" }}>3785.41 ml</td><td style={{ textAlign: "center" }}>16 cangkir</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Liter (L)</td><td style={{ textAlign: "center" }}>1000 ml</td><td style={{ textAlign: "center" }}>4.23 cangkir</td></tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Kepadatan Bahan Umum:</h3>
                <p style={{ lineHeight: "1.8" }}>
                  Untuk mengonversi volume ke berat, kita perlu mengetahui kepadatan bahan. Berikut beberapa nilai referensi:
                </p>
                <table className="table bmi-table" style={{ marginTop: "0.5rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Bahan</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Kepadatan (g/ml)</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Satu Cangkir (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Tepung terigu</td><td style={{ textAlign: "center" }}>0.529</td><td style={{ textAlign: "center" }}>125 g</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Gula putih</td><td style={{ textAlign: "center" }}>0.845</td><td style={{ textAlign: "center" }}>200 g</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Mentega</td><td style={{ textAlign: "center" }}>0.911</td><td style={{ textAlign: "center" }}>215 g</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Air / Susu</td><td style={{ textAlign: "center" }}>1.0 / 1.03</td><td style={{ textAlign: "center" }}>237 / 244 g</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Madu</td><td style={{ textAlign: "center" }}>1.42</td><td style={{ textAlign: "center" }}>336 g</td></tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Tips:</strong> Timbangan digital lebih akurat daripada cangkir saat memanggang. Selisih kecil berat tepung dapat mengubah tekstur adonan sepenuhnya. Gunakan konverter untuk memverifikasi jumlah sebelum memulai.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Konversi Ukuran Memasak</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Contoh 1: Konversi Cangkir ke mililiter untuk Kue</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Masalah:</strong> Resep memerlukan 2,5 cangkir susu dan Anda hanya memiliki ukuran dalam mililiter.</p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Konversi Volume ke Volume"</li>
                  <li>Dari: Cangkir AS — Ke: Mililiter</li>
                  <li>Masukkan: 2.5</li>
                  <li>Hasil: 591.47 ml</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan:</strong> 2.5 cangkir × 236.588 ml/cangkir = 591.47 ml
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 2: Konversi Tepung dari Cangkir ke Gram</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Masalah:</strong> Resep memerlukan 3 cangkir tepung dan Anda ingin mengukur dalam gram menggunakan timbangan.</p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Konversi Volume ke Berat"</li>
                  <li>Pilih bahan: Tepung terigu serbaguna</li>
                  <li>Dari: Cangkir AS — Ke: Gram</li>
                  <li>Masukkan: 3</li>
                  <li>Hasil: 375.57 gram</li>
                </ol>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 3: Konversi Ons ke Gram untuk Mentega</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Masalah:</strong> Resep memerlukan 4 oz mentega, berapa gramnya?</p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Konversi Berat ke Berat"</li>
                  <li>Dari: Ons — Ke: Gram</li>
                  <li>Masukkan: 4</li>
                  <li>Hasil: 113.4 gram</li>
                </ol>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 4: Konversi Gula dari Gram ke Sendok</h3>
                <p style={{ lineHeight: "1.8" }}><strong>Masalah:</strong> Anda menginginkan 50 gram gula putih dalam sendok makan.</p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Konversi Berat ke Volume"</li>
                  <li>Pilih bahan: Gula putih</li>
                  <li>Dari: Gram — Ke: Sendok makan</li>
                  <li>Masukkan: 50</li>
                  <li>Hasil: sekitar 4 sendok makan</li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Menggunakan Ukuran Memasak Secara Efektif</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Tips Umum:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Selalu gunakan timbangan untuk memanggang:</strong> Cangkir memberikan hasil yang bervariasi tergantung cara pengisian</li>
                  <li><strong>Pilih bahan yang benar:</strong> Saat mengonversi volume ke berat, kepadatan setiap bahan sangat berbeda</li>
                  <li><strong>Perhatikan jenis cangkir:</strong> Cangkir AS 237 ml, cangkir Australia 250 ml — konverter menggunakan ukuran AS</li>
                  <li><strong>Bahan kering:</strong> Aduk dengan garpu sebelum diukur dengan cangkir untuk menghindari penggumpalan</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Kesalahan Umum yang Perlu Dihindari:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Mencampuradukkan ons berat (oz) dan ons cair (fl oz)</li>
                  <li>Mengasumsikan satu cangkir tepung sama beratnya dengan satu cangkir gula</li>
                  <li>Mengisi cangkir dengan menekan bahan alih-alih meratakannya</li>
                  <li>Lupa mengonversi satuan penuh (misalnya: pint = 2 cangkir, bukan 1)</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Untuk Penggunaan Profesional:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Simpan konversi resep yang sering digunakan di catatan terpisah untuk menghemat waktu</li>
                  <li>Saat menggandakan resep, pastikan proporsi bahan kering tetap benar</li>
                  <li>Untuk restoran: konversi satuan resep ke kilogram dan liter untuk kemudahan</li>
                  <li>Selalu verifikasi kepadatan aktual bahan jika akurasi sangat penting</li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>⚠️ Peringatan:</strong> Nilai kepadatan dalam konverter adalah rata-rata standar. Nilai bahan tertentu mungkin berbeda tergantung merek atau metode persiapan. Untuk memanggang profesional, selalu gunakan timbangan untuk akurasi maksimum.
                </div>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>Pertanyaan yang Sering Diajukan</h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa sendok makan dalam satu cangkir?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Satu cangkir AS standar sama dengan <strong>16 sendok makan</strong>. Ini berarti seperempat cangkir = 4 sendok, dan setengah cangkir = 8 sendok. Angka yang mudah diingat dan sangat membantu saat mempersiapkan resep.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Sendok teh (tsp):</strong> Tiga sendok teh sama dengan satu sendok makan. Artinya satu cangkir = 48 sendok teh.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa mililiter dalam satu cangkir AS?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Satu cangkir AS standar sama dengan <strong>236.588 ml</strong>, dibulatkan dalam penggunaan sehari-hari menjadi 240 ml. Namun perhatikan: cangkir Australia = 250 ml, dan cangkir Inggris lama = 284 ml.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Contoh:</strong> Jika resep memerlukan 2 cangkir air, berarti sekitar 473 ml. Lebih mudah daripada menghitung setiap cangkir satu per satu!
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara mengonversi satu cangkir tepung ke gram?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Satu cangkir tepung terigu kira-kira sama dengan <strong>125 gram</strong>, berdasarkan kepadatan tepung (0.529 g/ml). Namun mungkin sedikit berbeda tergantung cara pengisian cangkir.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  Untuk menghindari variasi ini, aduk tepung terlebih dahulu dengan garpu, lalu masukkan ke cangkir perlahan dan ratakan dengan pisau. Dengan begitu Anda mendapatkan ukuran standar yang diasumsikan kebanyakan resep Amerika.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apa perbedaan antara ons cair dan ons berat?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Ini adalah salah satu sumber kebingungan terbesar di dapur. Ons cair (fl oz) adalah satuan <strong>volume</strong> yang sama dengan 29.57 ml, digunakan untuk mengukur cairan. Sedangkan ons berat (oz) adalah satuan <strong>massa</strong> yang sama dengan 28.35 gram, digunakan untuk bahan kering dan padat.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Contoh praktis:</strong> 8 fl oz air = 236 ml ≈ 236 g. Tapi 8 oz tepung ≈ 227 g yang setara dengan sekitar satu cangkir tiga perempat.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara mengonversi resep menjadi setengah atau dua kali lipat?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Untuk menjadikan resep setengahnya, bagi setiap jumlah dengan 2. Namun masalah muncul dengan jumlah seperti ¾ cangkir — setengahnya adalah ⅜ cangkir yang tidak praktis. Di sinilah konverter berguna: konversi ¾ cangkir ke ml (177 ml), setengahnya = 88.5 ml, lalu konversi ke sendok (sekitar 6 sendok makan).
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Untuk dua kali lipat:</strong> Kalikan jumlah dengan 2 lalu periksa apakah hasilnya dapat diukur dengan alat yang Anda miliki. Terkadang lebih mudah mengonversi 3 cangkir langsung ke ml dan mengukurnya dengan gelas ukur.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah timbangan lebih akurat dari cangkir saat memanggang?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Ya, tentu saja.</strong> Cangkir bergantung pada volume yang bervariasi tergantung tekanan pengisian dan cara pengukuran. Timbangan memberikan berat aktual terlepas dari faktor lainnya. Dalam memanggang, selisih 10 gram tepung dapat mengubah tekstur adonan sepenuhnya.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  Gunakan timbangan untuk bahan sensitif (tepung, gula, mentega) dan gunakan sendok untuk jumlah yang sangat kecil seperti garam dan baking powder.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa gram dalam satu sendok makan mentega?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Satu sendok makan mentega kira-kira sama dengan <strong>14 gram</strong>. Kepadatan mentega adalah 0.911 g/ml, dan volume sendok makan adalah 14.79 ml, yaitu: 14.79 × 0.911 ≈ 13.5 g.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  Kebanyakan stik mentega AS memiliki tanda yang menunjukkan sendok makan, setiap tanda = 1 sendok makan = 14 g. Ini membuat pengukuran sangat mudah tanpa timbangan.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Apakah konverter bekerja di ponsel?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Tentu saja!</strong> Konverter dirancang untuk bekerja dengan lancar di semua perangkat. Anda dapat menggunakannya di dapur langsung dari ponsel saat memasak tanpa perlu membuka komputer.
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li><strong>📱 Ponsel pintar:</strong> Antarmuka responsif yang nyaman untuk penggunaan satu tangan</li>
                  <li><strong>📲 Tablet:</strong> Ruang lebih besar dan tampilan hasil yang lebih jelas</li>
                  <li><strong>💻 Komputer:</strong> Pengalaman penuh dengan semua fitur</li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  Bekerja di semua browser modern tanpa aplikasi tambahan, dan hasilnya muncul seketika setiap kali ada perubahan input.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookingMeasurementCalculator;